import { isEmbedReference } from '@shared/ast'
import type { EmbedReference } from '@shared/ast/astTypes'
import { $getRoot, $isElementNode, type LexicalNode, type RangeSelection, createCommand } from 'lexical'

export const COPY_EMBED_COMMAND = createCommand<void>('COPY_EMBED_COMMAND')
export const PASTE_EMBED_COMMAND = createCommand<void>('PASTE_EMBED_COMMAND')
export const EMBED_CLIPBOARD_PREFIX = 'manifesto-embed:v1\n'
export const EMBED_BOOK_CLIPBOARD_TYPE = 'application/x-manifesto-book'

/** Include pending Lexical edits as well as committed state, but ignore focus/selection changes. */
export function $embedDestinationContent(): string {
  const serialize = (node: LexicalNode): unknown => {
    const value = node.exportJSON()
    return $isElementNode(node) ? { ...value, children: node.getChildren().map(serialize) } : value
  }
  return JSON.stringify(serialize($getRoot()))
}

export function selectionTouchesProtectedContent(selection: RangeSelection): boolean {
  const isProtected = (node: LexicalNode): boolean => {
    let current: LexicalNode | null = node
    while (current) {
      if (['link', 'custom-link', 'custom-encrypted'].includes(current.getType())) {
        return true
      }
      current = current.getParent()
    }
    return false
  }
  return [selection.anchor.getNode(), selection.focus.getNode(), ...selection.getNodes()].some(isProtected)
}

/** Links/encryption have text-only children; an inline object cannot be stored there. */
export function canInsertInlineEmbed(selection: RangeSelection): boolean {
  const container = (node: LexicalNode): LexicalNode | null => {
    let current: LexicalNode | null = node
    while (current) {
      if (['link', 'custom-link', 'custom-encrypted'].includes(current.getType())) {
        return null
      }
      if (
        [
          'paragraph',
          'custom-paragraph',
          'heading',
          'custom-heading',
          'quote',
          'custom-quote',
          'listitem',
          'custom-listitem',
        ].includes(current.getType())
      ) {
        return current
      }
      current = current.getParent()
    }
    return null
  }
  const start = container(selection.anchor.getNode())
  return Boolean(
    start &&
      start === container(selection.focus.getNode()) &&
      selection.getNodes().every((node) => container(node) === start),
  )
}

export interface EmbedClipboardPayload {
  bookId: string
  sources: EmbedReference[]
}

export function serializeEmbedClipboard(payload: EmbedClipboardPayload): string {
  return EMBED_CLIPBOARD_PREFIX + JSON.stringify(payload)
}

export function parseEmbedClipboard(text: string): EmbedClipboardPayload | null {
  if (!text.startsWith(EMBED_CLIPBOARD_PREFIX)) {
    return null
  }
  try {
    const value = JSON.parse(text.slice(EMBED_CLIPBOARD_PREFIX.length))
    if (
      typeof value?.bookId !== 'string' ||
      !value.bookId ||
      !Array.isArray(value.sources) ||
      value.sources.length === 0 ||
      value.sources.length > 500 ||
      !value.sources.every(isEmbedReference)
    ) {
      return null
    }
    return value as EmbedClipboardPayload
  } catch {
    return null
  }
}
